import { SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';

/** True when id is a persisted enterprise salon visit (not Hair Insight HA-*). */
export function isEnterpriseSalonSessionRef(sessionId) {
  const s = String(sessionId || '').trim();
  if (!s) return false;
  if (s.toUpperCase().startsWith('HA-')) return false;
  return true;
}

function coerceNumericId(...candidates) {
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const id = Number(raw);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

/** Accept workspace int ids or franchise price-service string ids (fps_…). */
function coerceTreatmentId(...candidates) {
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    const text = String(raw).trim();
    if (!text) continue;
    if (/^treatment_/i.test(text)) {
      const stripped = text.replace(/^treatment_/i, '');
      if (/^fps_/i.test(stripped)) return stripped;
      const asNum = Number(stripped);
      if (Number.isFinite(asNum) && asNum > 0) return asNum;
      continue;
    }
    if (/^fps_/i.test(text)) return text;
    const asNum = Number(text);
    if (Number.isFinite(asNum) && asNum > 0) return asNum;
  }
  return null;
}

export function buildProductCartPayload(items) {
  return Object.entries(items || {})
    .filter(([, item]) => item && Number(item.quantity) > 0)
    .map(([cartKey, item]) => {
      const rawId = item?.product?.id ?? item?.product?.product_id ?? cartKey;
      if (rawId == null || rawId === '') return null;
      const asNum = Number(rawId);
      const id = Number.isFinite(asNum) && asNum > 0 ? asNum : String(rawId).trim();
      if (id === '' || id === '0') return null;
      return {
        id,
        quantity: Number(item.quantity) || 1,
        product: item.product || undefined,
      };
    })
    .filter(Boolean);
}

export function buildTreatmentCartPayload(items) {
  return Object.entries(items || {})
    .filter(([, item]) => item && Number(item.quantity) > 0)
    .map(([cartKey, item]) => {
      const id = coerceTreatmentId(
        item?.treatment?.id,
        item?.treatment?.treatment_id,
        item?.id,
        cartKey,
      );
      if (id == null) return null;
      return { id, quantity: Number(item.quantity) || 1 };
    })
    .filter(Boolean);
}

export function persistProductCart(apiBaseUrl, sessionId, token, items) {
  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  if (!baseUrl || !sessionId) return Promise.resolve(null);
  const cart_products = buildProductCartPayload(items);
  return fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-cart-products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      skin_id: sessionId,
      session_id: sessionId,
      ...(token && { app_session_token: token }),
      cart_products,
    }),
    timeoutMs: 20000,
  }).catch(() => null);
}

export function persistTreatmentCart(apiBaseUrl, sessionId, token, items) {
  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  if (!baseUrl || !sessionId || !isEnterpriseSalonSessionRef(sessionId)) return Promise.resolve(null);
  const cart_treatments = buildTreatmentCartPayload(items);
  return fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-cart-treatments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      skin_id: sessionId,
      session_id: sessionId,
      ...(token && { app_session_token: token }),
      cart_treatments,
    }),
    timeoutMs: 20000,
  }).catch(() => null);
}
